/**
 * @file        src/components/SelectMenu.jsx
 * @description Shared dark-themed select / dropdown that replaces the native
 *              <select> (whose OS-rendered option list is white and cannot be
 *              themed). Renders a trigger button styled to match the field kit
 *              plus a portaled dark popover listing the options — the selected
 *              one filled neon, hover highlighted — mirroring SingleDatePicker /
 *              the PhoneInput country menu.
 *
 *              Controlled: parent owns `value` and gets `onChange(value)`.
 *              `options` is an array of strings, or { value, label } objects.
 *              `searchable` turns the trigger itself into a combobox: you type
 *              straight into the field to filter the list. Use it for long lists
 *              (countries) where scrolling alone is unusable.
 * @author      Kinetika Labs
 * @created     2026-06-26
 * @changelog
 *   - 2026-08-18 Claude — Icon refresh: the shared glyphs (close, chevrons, check,
 *                          map pin, arrows, search, calendar) redrawn as one
 *                          consistent filled 24×24 family. Path data only — every
 *                          <svg> wrapper, class and size is unchanged.
 *   - 2026-07-16 Claude — Theme fix: replaced hardcoded colors with theme tokens so the screen recolors on Light/Dark switch.
 *   - 2026-06-26 Claude — Initial. Replaces native <select> across Profile,
 *                          Insurance, GroupQuoteModal and PaxModal.
 *   - 2026-07-13 Claude — Added `searchable`: the trigger becomes the search
 *                          input (prefix-first matches, Enter picks the top hit,
 *                          arrows move). Options deduped by value — duplicate
 *                          React keys were stopping the list re-rendering.
 *   - 2026-07-13 Claude — `onSearch` + `loading`: the parent can serve the results
 *                          (server-side type-ahead). Local filtering is then off,
 *                          because a match may not be visible in the label at all
 *                          — "dubai" returns United Arab Emirates. `hint` on an
 *                          option shows what matched.
 */
import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

const normalize = (opt) =>
  (typeof opt === 'object' && opt !== null)
    ? { value: opt.value, label: opt.label ?? String(opt.value), hint: opt.hint ?? null }
    : { value: opt, label: String(opt), hint: null };

export default function SelectMenu({
  value,
  onChange,
  options = [],
  placeholder = 'Select',
  className = '',
  triggerClassName = '',
  buttonRef,
  searchable = false,
  onSearch,
  loading = false,
}) {
  // Dedupe by value. Options are `key`ed by value, and duplicates give React two
  // children with the same key — reconciliation then reuses stale DOM and the
  // list stops re-rendering when it is filtered. A bad upstream list must not be
  // able to break the menu, so we collapse it here rather than trust the caller.
  const opts = useMemo(() => {
    const seen = new Set();
    return options.map(normalize).filter((o) => {
      const k = String(o.value);
      if (seen.has(k)) return false;
      seen.add(k);
      return true;
    });
  }, [options]);
  const selected = opts.find((o) => o.value === value) || null;

  const [open, setOpen] = useState(false);
  const [rect, setRect] = useState(null);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const fieldRef = useRef(null);
  const popRef = useRef(null);
  const inputRef = useRef(null);

  // With `onSearch` the parent owns matching (it can search things the label does
  // not contain — a city, a code), so filtering here too would throw its results
  // away. Otherwise: prefix matches first, so "in" surfaces India before Argentina.
  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (onSearch || !searchable || !q) return opts;
    const starts = [];
    const contains = [];
    for (const o of opts) {
      const l = o.label.toLowerCase();
      if (l.startsWith(q)) starts.push(o);
      else if (l.includes(q)) contains.push(o);
    }
    return [...starts, ...contains];
  }, [opts, query, searchable, onSearch]);

  // Push the query out to the parent, which fetches and hands back new `options`.
  useEffect(() => { onSearch?.(query); }, [query]); // eslint-disable-line react-hooks/exhaustive-deps

  // Close on outside click / Escape.
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (ev) => {
      if (popRef.current?.contains(ev.target)) return;
      if (fieldRef.current?.contains(ev.target)) return;
      setOpen(false);
    };
    const onKey = (ev) => { if (ev.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    window.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      window.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // Anchor the popover to the trigger, re-measuring on scroll/resize; flip up
  // when it would overflow the viewport bottom.
  useLayoutEffect(() => {
    if (!open) return undefined;
    const place = () => {
      const r = fieldRef.current?.getBoundingClientRect();
      if (!r) return;
      const h = popRef.current?.offsetHeight || 240;
      let top = r.bottom + 6;
      if (top + h > window.innerHeight - 8) {
        const above = r.top - h - 6;
        top = above > 8 ? above : Math.max(8, window.innerHeight - h - 8);
      }
      setRect({ top, left: r.left, width: r.width });
    };
    place();
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    return () => {
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
    };
  }, [open]);

  // A stale query would leave the list filtered the next time it opens.
  useEffect(() => {
    if (!open) return;
    setQuery('');
    setActive(0);
    if (searchable) inputRef.current?.focus();
  }, [open, searchable]);

  const pick = (v) => { onChange?.(v); setOpen(false); };

  const onSearchKey = (ev) => {
    if (ev.key === 'ArrowDown' || ev.key === 'ArrowUp') {
      ev.preventDefault();
      setActive((i) => {
        const n = shown.length;
        if (!n) return 0;
        return (i + (ev.key === 'ArrowDown' ? 1 : -1) + n) % n;
      });
    } else if (ev.key === 'Enter') {
      ev.preventDefault();
      if (shown[active]) pick(shown[active].value);
    }
  };

  const setRefs = (el) => {
    fieldRef.current = el;
    if (typeof buttonRef === 'function') buttonRef(el);
    else if (buttonRef) buttonRef.current = el;
  };

  const chevron = (
    <svg viewBox="0 0 24 24" className={'w-4 h-4 text-muted shrink-0 transition-transform ' + (open ? 'rotate-180' : '')}>
      <path fill="currentColor" d="M12 15.4 5.8 9.2l1.6-1.6 4.6 4.6 4.6-4.6 1.6 1.6z" />
    </svg>
  );

  const wrapCls = 'w-full text-left flex items-center justify-between gap-2 outline-none '
    + (triggerClassName || 'bg-transparent text-[15px]') + ' ' + className;

  return (
    <>
      {searchable ? (
        // The field IS the search box — you type where you read. A separate input
        // inside the popover made the user hunt for a second place to type.
        <div ref={setRefs} className={wrapCls + ' cursor-text'} onClick={() => { setOpen(true); inputRef.current?.focus(); }}>
          <input
            ref={inputRef}
            type="text"
            role="combobox"
            aria-expanded={open}
            autoComplete="off"
            value={open ? query : (selected?.label ?? '')}
            onChange={(e) => { setQuery(e.target.value); setActive(0); if (!open) setOpen(true); }}
            onFocus={() => setOpen(true)}
            onKeyDown={onSearchKey}
            placeholder={selected ? selected.label : placeholder}
            className="w-full bg-transparent outline-none placeholder:text-muted/60"
          />
          {chevron}
        </div>
      ) : (
        <button
          type="button"
          ref={setRefs}
          onClick={() => setOpen((o) => !o)}
          className={wrapCls + ' cursor-pointer'}
        >
          <span className={selected ? '' : 'text-muted/60'}>{selected ? selected.label : placeholder}</span>
          {chevron}
        </button>
      )}

      {open && rect && createPortal(
        <div
          ref={popRef}
          data-nh-popover=""
          style={{ position: 'fixed', top: rect.top, left: rect.left, minWidth: rect.width, zIndex: 1000 }}
          className="rounded-xl border border-line bg-panel shadow-2xl overflow-hidden"
        >
          <div className="py-1.5 max-h-72 overflow-auto no-scrollbar">
            {shown.length === 0 && (
              <p className="px-4 py-3 text-[13px] text-muted">
                {loading ? 'Searching…' : `No matches for “${query}”`}
              </p>
            )}
            {shown.map((o, i) => {
              const isSel = o.value === value;
              const isActive = searchable && query && i === active;
              return (
                <button
                  key={String(o.value)}
                  type="button"
                  onClick={() => pick(o.value)}
                  onMouseEnter={() => setActive(i)}
                  className={
                    'w-full text-left px-4 py-2 text-[14px] cursor-pointer transition flex items-center justify-between gap-3 '
                    + (isSel ? 'bg-neon/15 text-neon font-semibold'
                             : isActive ? 'bg-ink/5 text-ink' : 'text-ink hover:bg-ink/5')
                  }
                >
                  <span className="min-w-0 truncate">
                    {o.label}
                    {/* Why this row answers a query that isn't in its label — e.g.
                        typing "dubai" returns United Arab Emirates · Dubai. */}
                    {o.hint && <span className="text-muted font-normal"> · {o.hint}</span>}
                  </span>
                  {isSel && (
                    <svg viewBox="0 0 24 24" className="w-4 h-4 shrink-0"><path fill="currentColor" d="M9 16.2 4.8 12l-1.4 1.4L9 19 21 7l-1.4-1.4z" /></svg>
                  )}
                </button>
              );
            })}
          </div>
        </div>,
        document.body,
      )}
    </>
  );
}
